import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    MenuItem,
    TextField,
} from "@mui/material";
import HESPFormLabel from "../../../HESPFormLabel/HESPFormLabel";
import { useEffect, useState } from "react";
import SwitchWithTitle from "../../../SwitchWithTitle/SwitchWithTitle";
import { useMutation } from "react-apollo";
import { CREATE_V2_CHANNEL } from "./V2ChannelDetails";
import { toast } from "react-toastify";
import { GraphQLError } from "graphql";
import { CreateV2Channel, CreateV2ChannelVariables } from "./__generated__/CreateV2Channel";
import { GET_V2_CHANNELS_OF_ORGANIZATION } from "../V2ChannelsOverview";
import { navigate } from "@reach/router";
import { GetOrganizationBasics_organization } from "../../../GetStarted/__generated__/GetOrganizationBasics";

export interface Props {
    open: boolean;
    onClose: () => void;
    organization: GetOrganizationBasics_organization;
}

export default function CreateV2ChannelDialog({ onClose, open, organization }: Props) {
    const [name, setName] = useState<string>("");
    const [externalId, setExternalId] = useState<string>("");

    const accessToAds = organization.channelSettings?.theoads?.enabled === true;

    const [dvrEnabled, setDvrEnabled] = useState<boolean>(false);
    const [dvrWindow, setDvrWindow] = useState<number>(3600);

    const [ads, setAds] = useState<boolean>(false);
    const [nielsen, setNielsen] = useState<boolean>(false);

    const [assetKey, setAssetKey] = useState<string>("");
    const [layout, setLayout] = useState<string>("SINGLE");
    const [enableBumperAds, setEnableBumperAds] = useState<boolean>(false);
    const [podDuration, setPoduration] = useState<number>(90);

    useEffect(() => {
        setName("");
        setExternalId("");
        setAds(false);
        setNielsen(false);

        setAssetKey("");
        setLayout("SINGLE");
        setEnableBumperAds(false);
        setPoduration(90);
    }, [open]);

    const [createV2Channel, { loading }] = useMutation<CreateV2Channel, CreateV2ChannelVariables>(CREATE_V2_CHANNEL);

    async function onCreate(): Promise<void> {
        try {
            const res = await createV2Channel({
                variables: {
                    input: {
                        organizationId: organization.organizationId,
                        name,
                        externalId: externalId.trim().length > 0 ? externalId : undefined,
                        dvr: {
                            enabled: dvrEnabled,
                            windowInSeconds: dvrWindow,
                        },
                        nielsen: {
                            enabled: nielsen,
                        },
                        ads: accessToAds
                            ? {
                                  enabled: ads,
                                  settings: ads
                                      ? {
                                            assetKey,
                                            enableBumperAds,
                                            layout,
                                            podDuration,
                                        }
                                      : undefined,
                              }
                            : undefined,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_V2_CHANNELS_OF_ORGANIZATION,
                        variables: { organizationId: organization.organizationId },
                    },
                ],
            });
            toast.success("Channel created");
            if (res?.data?.createV2Channel?.id) {
                navigate(`/app/${organization.organizationId}/v2-channels/${res.data?.createV2Channel.id}`);
            }
            onClose();
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
    }

    return (
        <Dialog fullWidth open={open}>
            <DialogTitle>Create channel</DialogTitle>
            <DialogContent className="flex flex-col gap-2">
                <FormControl>
                    <HESPFormLabel label="Name" />
                    <TextField value={name} onChange={(e) => setName(e.target.value)} />
                </FormControl>
                <FormControl>
                    <HESPFormLabel label="External ID" />
                    <TextField value={externalId} onChange={(e) => setExternalId(e.target.value)} />
                </FormControl>
                {accessToAds && (
                    <>
                        <FormControl>
                            <HESPFormLabel label="Ads" />
                            <SwitchWithTitle checked={ads} onChange={() => setAds(!ads)} title="Enable ads" />
                        </FormControl>
                        {ads && (
                            <>
                                <FormControl>
                                    <HESPFormLabel label="Asset key" />
                                    <TextField value={assetKey} onChange={(e) => setAssetKey(e.target.value)} />
                                </FormControl>
                                <FormControl>
                                    <HESPFormLabel label="Layout" />
                                    <TextField select value={layout} onChange={(e) => setLayout(e.target.value)}>
                                        {["SINGLE", "DOUBLE", "LSHAPE_AD", "LSHAPE_CONTENT"].map((lay) => (
                                            <MenuItem value={lay}>{lay}</MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                                <FormControl>
                                    <HESPFormLabel label="Bumper ads" />
                                    <SwitchWithTitle
                                        checked={enableBumperAds}
                                        onChange={() => setEnableBumperAds(!enableBumperAds)}
                                        title="Enable bumper ads"
                                    />
                                </FormControl>
                                {enableBumperAds && (
                                    <FormControl>
                                        <HESPFormLabel label="Pod duration" />
                                        <TextField
                                            type="number"
                                            InputProps={{
                                                inputProps: { min: 0, max: 1_000, step: 1 },
                                            }}
                                            value={podDuration}
                                            onChange={(e) => setPoduration(parseInt(e.target.value))}
                                        />
                                    </FormControl>
                                )}
                            </>
                        )}
                    </>
                )}

                <FormControl>
                    <HESPFormLabel label="Nielsen" />
                    <SwitchWithTitle checked={nielsen} onChange={() => setNielsen(!nielsen)} title="Enable Nielsen" />
                </FormControl>

                <FormControl>
                    <HESPFormLabel label="DVR" />
                    <SwitchWithTitle
                        checked={dvrEnabled}
                        onChange={() => setDvrEnabled(!dvrEnabled)}
                        title="Enable DVR"
                    />
                </FormControl>

                {dvrEnabled && (
                    <>
                        <FormControl>
                            <HESPFormLabel label="DVR window (in seconds)" />
                            <TextField
                                type="number"
                                InputProps={{
                                    inputProps: { min: 60, max: 60 * 60 * 24, step: 1 },
                                }}
                                value={dvrWindow}
                                onChange={(e) => setDvrWindow(parseInt(e.target.value))}
                            />
                        </FormControl>
                    </>
                )}
            </DialogContent>
            <DialogActions className=" flex flex-row gap-2 justify-end">
                <Button variant="contained" color="inherit" onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" disabled={loading} onClick={onCreate}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
}
